/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import {useSelector} from "react-redux";
import {selectAllSpecies} from "../../../Redux/Selectors/speciesSelectors";
import {selectSurveyPhotos} from "../../../Redux/Selectors/photoSelectors";
import {useGetUserFacingSpeciesNameById} from "../../../Hooks/LabelHooks";
import {
    competitionLifeStageOptions,
    insectsImpactCausingOptions,
    insectsImpactTypeOptions
} from "../../../Constants/survey";
import {ReviewedPhotoThumbnail} from "../../Pages/Events/ReviewedPhotoThumbnail";
import {Table} from "react-bootstrap";
import {hasMonitoringRecommendations} from "../../../Utils/SurveyUtils";
import LegacyDataInfoIcon from "../LegacyDataInfoIcon";
import React from "react";
import {
    selectQualitativeVegetationMonitoringBySampleEventId,
    selectQualitativeVegetationMonitoringRecommendationsBySampleEventId,
    selectQualitativeVegetationMonitoringRepeatersBySampleEventId
} from "../../../Redux/Selectors/qualitativeSelectors";

/**
 * NOTE: that this component logic mirrors the displayValueForQualitativeVegetationQuestion function from
 * SurveyService.php so if you fix bugs or make changes in one you should consider parallel changes in the other.
 * @param props
 * @returns {(*)[]|null}
 * @constructor
 */
export const SurveyQuestionAnswer = (props) => {
    
    const {event} = props;
    
    const fieldTypeFromSurveyQuestionTypeId = {
        1: 'text',
        13: 'radio',
        14: 'select',
        15: 'many-fields',
        16: 'monitoring-recommendations',
        17: 'monitoring-repeater',
    };
    
    const speciesList = useSelector(state => selectAllSpecies(state, event.projectId));
    const surveyPhotos = useSelector(state => selectSurveyPhotos(state, event.sampleEventId));
    
    const getUserFacingSpeciesNameById = useGetUserFacingSpeciesNameById();
    const getColumnsByCode = (code, type = null) => {
        if (type === 'monitoring-recommendations') {
            return [{ title: 'Recommendations', code: 'recommendationNotes' }]
        }
        
        switch (code) {
            case 'flood':
            case 'fire':
            case 'drift':
                return [
                    { title: 'Photo', code: 'photoId', isRequired: false },
                    { title: 'Latitude ° N', code: 'latitude', isRequired: true },
                    { title: 'Longitude ° E', code: 'longitude', isRequired: true },
                    { title: 'Area impacted', code: `${code}Impacted`, isRequired: true },
                ];
            case 'insects':
                return [
                    { title: 'Photo', code: 'photoId', isRequired: false },
                    {
                        title: 'Impact type', code: 'insectsImpactType', options: insectsImpactTypeOptions,
                        isRequired: true
                    },
                    {
                        title: 'Cause Identified', code: 'insectsImpactCausing', options: insectsImpactCausingOptions,
                        isRequired: true
                    },
                    // {title: 'Species Name', code: 'insectsSpeciesId', isRequired: true},
                    { title: 'Description', code: 'insectsOrDiseasesDescription', isRequired: true },
                ];
            case 'insectsii':
                return [
                    { title: 'Photo', code: 'photoId', isRequired: false },
                    {
                        title: 'Impact type', code: 'insectsImpactType', options: insectsImpactTypeOptions,
                        isRequired: true
                    },
                    {
                        title: 'Species Identified', code: 'insectsImpactCausing', options: insectsImpactCausingOptions,
                        isRequired: true
                    },
                    { title: 'Species Name', code: 'insectsSpeciesId', isRequired: true },
                    { title: 'Description', code: 'insectsOrDiseasesDescription', isRequired: true },
                ];
            case 'browse':
                return [
                    { title: 'Photo', code: 'photoId', isRequired: false },
                    { title: 'Species Name', code: 'browseSpeciesId', isRequired: true },
                    { title: '% of plants impacted', code: 'browseImpactedSpecies', isRequired: true },
                    { title: 'Notes', code: 'browseAnimalNote' },
                ];
            case 'soils':
                return [
                    { title: 'Latitude ° N', code: 'latitude', isRequired: true },
                    { title: 'Longitude ° E', code: 'longitude', isRequired: true },
                    { title: 'Possible Reasons', code: 'soilsReasonsNote', isRequired: false },
                ];
            case 'health':
                return [
                    { title: 'Species Name', code: 'healthSpeciesId', isRequired: true },
                    { title: 'Suspected Cause', code: 'healthSpeciesCause', isRequired: false },
                ];
            case 'performance':
                return [
                    { title: 'Species Name', code: 'performanceSpeciesId', isRequired: true },
                ];
            case 'competition':
                return [
                    { title: 'Species Name', code: 'competitionSpeciesId', isRequired: true },
                    { title: 'Estimated % Cover', code: 'competitionEstimatedCover', isRequired: true },
                    {
                        title: 'Life Stage', code: 'competitionLifeStage', options: competitionLifeStageOptions,
                        isRequired: true
                    },
                ];
            default:
                return [];
        }
    };
    
    
    const formatSubTableValue = (repeater, column) => {
        let code = column.code;
        let value = repeater[code];
        
        if (code.endsWith('SpeciesId')) {
            if (value) {
                value = getUserFacingSpeciesNameById(speciesList, value);
            } else {
                // Remove "Id" from code
                code = code.slice(0, -2);
                value = repeater[code];
            }
        }
        
        if (code === 'photoId') {
            let photos = surveyPhotos
                .filter(photo => (String(photo.qualitativeVegetationMonitoringRepeaterId) === String(repeater.qualitativeVegetationMonitoringRepeaterId)));
            value = photos.length ? photos.map(photo => <ReviewedPhotoThumbnail photoId={String(photo.photoId)}/>) :
                <ReviewedPhotoThumbnail photoId={null}/>;
        }
        
        if (column.options) {
            const optionValue = column.options.find(option => (option.id ?? option.value) === repeater[code])
            value = optionValue?.name ?? optionValue?.label ?? value;
        }
        
        if ((column?.isRequired ?? false) && (value == null || value === '')) {
            value = <span className="no-data-message">No Data</span>;
        }
        
        return <td key={code}>{value}</td>;
    };
    
    const renderSubTable = (columns, code, type) => {
        
        const recommendations = useSelector(state => selectQualitativeVegetationMonitoringRecommendationsBySampleEventId(state, event.sampleEventId));
        const repeaters = useSelector(state => selectQualitativeVegetationMonitoringRepeatersBySampleEventId(state, event.sampleEventId));
        const rows = (type === 'monitoring-recommendations') ?
            recommendations?.filter(recommendation => recommendation.code === code) :
            repeaters?.filter(repeater => repeater.code === code);
        
        // const rows = (type === 'monitoring-recommendations') ?
        //     answers?.QualitativeVegetationMonitoringRecommendations?.filter(recommendation => recommendation.code === code) :
        //     answers?.QualitativeVegetationMonitoringRepeaters?.filter(repeater => repeater.code === code);
        //
        return <div className="sub-table" key={code}>
            <Table borderless responsive>
                <thead>
                {type !== 'monitoring-recommendations' ?
                    <tr>
                        {
                            columns.map(column => (
                                <th key={column.code}>{column.title}</th>
                            ))
                        }
                    </tr> : null
                }
                </thead>
                <tbody>
                {
                    !rows?.length &&
                    <tr key='no-data-row' style={{ "textAlign": "center" }}>
                        <td colSpan={20} key='no-data-col'>
                            <span className="no-data-message">No Data</span>
                        </td>
                    </tr>
                }
                {
                    (type === 'monitoring-recommendations') ?
                        rows?.map(recommendation => (
                            <tr key={recommendation.qualitativeVegetationMonitoringRecommendationId}>
                                {
                                    columns.map(column => (
                                        <td key={column.code}>{recommendation[column.code]}</td>
                                    ))
                                }
                            </tr>
                        )) :
                        rows?.map(repeater => (
                            <tr key={repeater.qualitativeVegetationMonitoringRepeaterId}>
                                {
                                    columns.map(column => formatSubTableValue(repeater, column))
                                }
                            </tr>
                        ))
                    
                }
                </tbody>
            </Table>
        </div>
    };
    
    const { question: fieldDefinition, answer } = props;
    
    const answers = useSelector(state => selectQualitativeVegetationMonitoringBySampleEventId(state, event.sampleEventId));
    
    if(!fieldDefinition) {
        return null;
    }
    
    let result = [];
    let secondaryFields = [];
    
    const fieldType = fieldDefinition.type ?? fieldTypeFromSurveyQuestionTypeId[fieldDefinition.surveyQuestionDataTypeId];
    
    const defaultValues = [{ label: 'Yes', value: '1' }, { label: 'No', value: '0' }];
    const optionValues = fieldDefinition?.options?.values ?? fieldDefinition?.values ?? defaultValues;
    
    switch (fieldType) {
        case 'radio':
        case 'select':
            const matchedOptionValue = optionValues.find(({ value: optionValue }) => {
                return String(optionValue?.value ?? optionValue) === String(answer);
            });
            // Don't show label if value is Yes on Monitoring Recommendations
            if (!(matchedOptionValue?.label === 'Yes' && hasMonitoringRecommendations(fieldDefinition))) {
                result.push(matchedOptionValue?.label ?? matchedOptionValue?.value ??
                    (answer != null ? <span key={answer?.surveyAnswerId}
                                            className="legacy-data-message">{String(answer)}<LegacyDataInfoIcon/></span> : null));
            }
            secondaryFields = matchedOptionValue?.secondaryFields ?? [];
            break;
        case 'many-fields':
            const checkedValues = optionValues.filter(option => !!(answers?.[option.code]));
            result = result.concat(checkedValues.map(option => option.label ?? option?.value ?? 'N/A').join(', '));
            secondaryFields = checkedValues.reduce((acc, checkedValue) => acc.concat(checkedValue?.secondaryFields ?? []), []);
            break;
        case 'monitoring-recommendations':
        case 'monitoring-repeater':
            const columns = getColumnsByCode(fieldDefinition.code, fieldDefinition.type);
            result.push(renderSubTable(columns, fieldDefinition.code, fieldDefinition.type));
            break;
        case 'text':
        default:
            result.push(answer);
            break;
    }
    
    // get display values for secondary fields
    let secondaryFieldResults = secondaryFields.map((field) => {
        // the PHP version also uses implode here, but
        // .join(' ') breaks objects by turning them to [Object object]
        // and is unnecessary since React can render arrays directly
        return <SurveyQuestionAnswer
            event={event}
            question={field}
            answer={answers?.[field.code]}
        />
    });
    
    // remove nulls
    secondaryFieldResults = secondaryFieldResults.filter(x => x !== null);
    
    if (result.length && secondaryFieldResults.length) {
        result.push(' : ');
    }
    
    return result.map((answer, i) => {
        if ((fieldDefinition?.isRequired ?? false) && (answer == null || answer === '')) {
            return <span key={i} className="no-data-message">No Data</span>;
        }
        
        return answer;
    }).concat(secondaryFieldResults);
};
